import { config } from '@/forms/config';
import { ValidationAdapter } from '@/forms/core';
import type {
  AtLeastOneChildAnsweredYesRestriction,
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
  FormValidationSchema,
  QuestionWithDetailsFormField,
} from '@/forms/typings';
import { FormFieldType } from '@/forms/typings';
import { generateConditionalValidation } from './generateConditionalValidation';
import { generateConditionalValidationFieldIds } from './generateConditionalValidationFieldIds';

/** True when the child is a question answered Yes and not marked Not Applicable. */
const isAnsweredYes = (
  field: FormField,
  root: Record<string, unknown>,
): boolean => {
  if (field.type !== FormFieldType.QuestionWithDetails) return false;

  const naFieldId = (field as QuestionWithDetailsFormField).notApplicableField
    ?.fieldId;

  if (naFieldId && root[naFieldId] === true) return false;

  return (
    root[`${field.fieldId}${config.questionWithDetailsFieldSuffix}`] === 'yes'
  );
};

/**
 * Yup schema for a non-editable Formik key that validates "at least one of the
 * group's question children is answered Yes". Gated by the group's visibility
 * conditions (should-validate, QWD branch, N/A) so hidden subtrees never block submit.
 */
export const createAtLeastOneChildAnsweredYesYupSchema = (
  children: FormField[],
  restriction: AtLeastOneChildAnsweredYesRestriction,
  conditions: FormConditionalValidation[] | undefined,
) => {
  const Yup = ValidationAdapter.getLibrary();
  const message =
    restriction.message?.trim() ||
    'Answer Yes to at least one of these questions.';

  const base = Yup.mixed().nullable();

  const tested = base.test({
    name: 'atLeastOneChildAnsweredYes',
    message,
    test() {
      const root = this.parent as Record<string, unknown> | undefined;
      if (!root) return true;

      return children.some(child => isAnsweredYes(child, root));
    },
  });

  if (!conditions?.length) return tested;

  return base.when(
    generateConditionalValidationFieldIds(conditions),
    (values, schema) =>
      generateConditionalValidation(conditions, values) ? tested : schema,
  );
};

/** The synthetic Formik key for a group's "at least one child answered Yes" rule. */
export const atLeastOneChildAnsweredYesKey = (groupFieldId: string): string =>
  `${groupFieldId}${config.atLeastOneChildAnsweredYesFieldSuffix}`;

/**
 * Registers the synthetic "at least one child answered Yes" Formik key (seeded
 * undefined so it is touched on submit) and its gated schema, for one group.
 */
export const registerAtLeastOneChildAnsweredYesValidation = (
  initialValues: FormInitialValues,
  validationFields: FormValidations,
  groupFieldId: string,
  children: FormField[],
  restriction: AtLeastOneChildAnsweredYesRestriction,
  conditions: FormConditionalValidation[] | undefined,
): void => {
  const key = atLeastOneChildAnsweredYesKey(groupFieldId);
  initialValues[key] = undefined;
  validationFields[key] = createAtLeastOneChildAnsweredYesYupSchema(
    children,
    restriction,
    conditions,
  ) as FormValidationSchema;
};
